import React, { useEffect, useRef, useState } from 'react';
import { useTableScrollIndicators } from '@hooks/useTableScrollIndicators';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import type { ColumnDefinition, SortOrder, TableAction } from './types';

interface BillingTableProps<T extends Record<string, unknown>> {
  data: T[];
  columns: ColumnDefinition<T>[];
  totalItems: number;
  page: number;
  perPage: number;
  sortField?: string;
  sortOrder?: SortOrder;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
  onRowClick?: (row: T) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  actions?: TableAction<T>[];
  loading?: boolean;
  selectable?: boolean;
  emptyMessage?: string;
}

export function BillingTable<T extends Record<string, unknown> & { id: string }>({
  data,
  columns,
  totalItems,
  page,
  perPage,
  sortField,
  sortOrder = 'asc',
  onPageChange,
  onPerPageChange,
  onSort,
  onRowClick,
  onSelectionChange,
  actions = [],
  loading = false,
  selectable = true,
  emptyMessage = 'No data available',
}: BillingTableProps<T>) {
  const [selected, setSelected] = useState<string[]>([]);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selected);
    }
  }, [selected, onSelectionChange]);

  // Ref for scroll container
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isPhoneScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTabletOrLarger = useMediaQuery(theme.breakpoints.up('sm')); // >= 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Use scroll indicators hook - always enabled for overflow detection
  const scrollIndicators = useTableScrollIndicators(tableContainerRef, true);

  const handleSelectAllClick = () => {
    // Gmail-style behavior: if any are selected (indeterminate or all), deselect all
    // Otherwise, select all
    if (selected.length > 0) {
      // If any are selected, deselect all
      setSelected([]);
    } else {
      // If none selected, select all
      const newSelected = data.map(row => row.id);
      setSelected(newSelected);
    }
  };

  const handleSelectClick = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <Box sx={{ opacity: 0.3, ml: 0.5, display: 'inline-flex' }}>
          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        </Box>
      );
    }
    return (
      <Box sx={{ ml: 0.5, display: 'inline-flex' }}>
        {sortOrder === 'asc' ? (
          <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        )}
      </Box>
    );
  };

  const renderCellContent = (column: ColumnDefinition<T>, row: T) => {
    const value = row[column.field as keyof T];

    if (column.format) {
      return column.format(value, row);
    }

    // Default formatting for common fields
    if (column.field === 'status' && typeof value === 'string') {
      const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
        ready: 'success',
        pending: 'warning',
        draft: 'default',
        submitted: 'info',
        accepted: 'success',
        denied: 'error',
        completed: 'success',
      };
      return (
        <Chip
          label={value}
          size="small"
          color={statusColors[value] || 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      );
    }

    if (
      typeof value === 'number' &&
      (column.field === 'amount' || column.field === 'totalCharges')
    ) {
      return `$${value.toLocaleString()}`;
    }

    return String(value ?? '');
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={60} />
            ))}
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            p: 8,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6" gutterBottom>
            {emptyMessage}
          </Typography>
          <Typography variant="body2">Try adjusting your filters or check back later</Typography>
        </Box>
      </Paper>
    );
  }

  // Filter columns based on screen size
  const visibleColumns = columns.filter(col => {
    if (isPhoneScreen && col.mobileHidden) return false;
    if (isTablet && col.priority === 'low') return false;
    return true;
  });

  // Calculate sticky offset for columns
  const getStickyOffset = (columnIndex: number): number => {
    let offset = 0;
    if (selectable) offset = 48; // Checkbox width

    for (let i = 0; i < columnIndex; i++) {
      const col = visibleColumns[i];
      if (col.sticky) {
        offset += col.width || 150;
      }
    }
    return offset;
  };

  return (
    <>
      {/* Wrapper for table with overflow indicators */}
      <Box
        sx={{
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden', // Hide overflow to contain gradients
        }}
      >
        {/* Left scroll indicator - show on all screen sizes when scrolled */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: isPhoneScreen ? 20 : 30, // Narrower on phones
            pointerEvents: 'none',
            zIndex: 3,
            background: 'linear-gradient(to right, rgba(250,250,250,0.95), transparent)',
            opacity: scrollIndicators.showLeftIndicator ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />

        {/* Right scroll indicator - show on all screen sizes when overflow exists */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            width: isPhoneScreen ? 20 : 30, // Narrower on phones
            pointerEvents: 'none',
            zIndex: 3,
            background: 'linear-gradient(to left, rgba(250,250,250,0.95), transparent)',
            opacity: scrollIndicators.showRightIndicator ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />

        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          elevation={0}
          sx={{
            overflowX: 'auto',
            position: 'relative',
            border: 'none', // Remove border since parent has it
            borderRadius: 0, // Remove radius since parent has it
            // Custom scrollbar for better mobile experience
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: 4,
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ borderBottom: '2px solid #e0e0e0' }}>
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      width: 48,
                      position: isTabletOrLarger ? 'sticky' : 'relative',
                      left: 0,
                      zIndex: 4,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < data.length}
                      checked={data.length > 0 && selected.length === data.length}
                      onChange={handleSelectAllClick}
                      size="small"
                    />
                  </TableCell>
                )}
                {visibleColumns.map((column, columnIndex) => (
                  <TableCell
                    key={String(column.field)}
                    align={column.align || 'left'}
                    sx={{
                      color: '#424242',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      cursor: column.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      width: column.width,
                      minWidth: column.width,
                      bgcolor: '#fafafa',
                      ...(column.sticky && isTabletOrLarger
                        ? {
                            position: 'sticky',
                            left: getStickyOffset(columnIndex),
                            zIndex: 3,
                            borderRight: scrollIndicators.showStickyColumnShadow
                              ? '1px solid #e0e0e0'
                              : 'none',
                            boxShadow:
                              scrollIndicators.showStickyColumnShadow && isTabletOrLarger
                                ? '2px 0 5px -2px rgba(0,0,0,0.1)'
                                : 'none',
                            transition: 'box-shadow 0.2s ease-in-out',
                          }
                        : {}),
                      '&:hover': column.sortable
                        ? {
                            bgcolor: 'action.hover',
                          }
                        : {},
                    }}
                    onClick={() => {
                      if (column.sortable) {
                        onSort(String(column.field));
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {column.header}
                      {column.sortable && <SortIcon field={String(column.field)} />}
                    </Box>
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell
                    align="right"
                    sx={{ minWidth: 180, pr: 2, whiteSpace: 'nowrap', bgcolor: '#fafafa' }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isItemSelected}
                    sx={{
                      bgcolor: index % 2 === 0 ? 'transparent' : '#fafafa',
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {selectable && (
                      <TableCell
                        padding="checkbox"
                        sx={{
                          position: isTabletOrLarger ? 'sticky' : 'relative',
                          left: 0,
                          zIndex: 2,
                          ...(scrollIndicators.showStickyColumnShadow && isTabletOrLarger
                            ? {
                                bgcolor: index % 2 === 0 ? 'background.paper' : '#fafafa',
                              }
                            : {}),
                        }}
                      >
                        <Checkbox
                          checked={isItemSelected}
                          onClick={e => {
                            e.stopPropagation();
                            handleSelectClick(row.id);
                          }}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {visibleColumns.map((column, columnIndex) => (
                      <TableCell
                        key={String(column.field)}
                        align={column.align || 'left'}
                        sx={{
                          fontSize: '0.875rem',
                          ...(typeof row[column.field as keyof T] === 'number' && {
                            fontFamily: 'monospace',
                          }),
                          // Prevent wrapping for annotations column
                          ...(column.field === 'annotations' && {
                            whiteSpace: 'nowrap',
                          }),
                          ...(column.sticky && isTabletOrLarger
                            ? {
                                position: 'sticky',
                                left: getStickyOffset(columnIndex),
                                zIndex: 1,
                                ...(scrollIndicators.showStickyColumnShadow
                                  ? {
                                      bgcolor: index % 2 === 0 ? 'background.paper' : '#fafafa',
                                      borderRight: '1px solid #e0e0e0',
                                      boxShadow: '2px 0 5px -2px rgba(0,0,0,0.05)',
                                    }
                                  : {}),
                                transition: 'box-shadow 0.2s ease-in-out',
                              }
                            : {}),
                        }}
                      >
                        {renderCellContent(column, row)}
                      </TableCell>
                    ))}
                    {actions.length > 0 && (
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {actions
                            .filter(action => {
                              // Check if action should be hidden
                              if (!action.hidden) return true;
                              const isHidden =
                                typeof action.hidden === 'function'
                                  ? action.hidden(row)
                                  : action.hidden;
                              return !isHidden;
                            })
                            .map((action, index) => {
                              const label =
                                typeof action.label === 'function'
                                  ? action.label(row)
                                  : action.label;
                              const icon =
                                typeof action.icon === 'function' ? action.icon(row) : action.icon;
                              const ariaLabel = action.ariaLabel
                                ? typeof action.ariaLabel === 'function'
                                  ? action.ariaLabel(row)
                                  : action.ariaLabel
                                : label;
                              const isDisabled =
                                typeof action.disabled === 'function'
                                  ? action.disabled(row)
                                  : action.disabled;

                              return (
                                <Button
                                  key={index}
                                  size="small"
                                  variant={action.variant || 'outlined'}
                                  color={action.color || 'primary'}
                                  startIcon={icon}
                                  aria-label={ariaLabel}
                                  disabled={isDisabled}
                                  onClick={e => {
                                    e.stopPropagation();
                                    action.onClick(row);
                                  }}
                                  sx={{
                                    minWidth: 'auto',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1.5,
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {label}
                                </Button>
                              );
                            })}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        component="div"
        count={totalItems}
        page={page - 1}
        onPageChange={(_, newPage) => {
          onPageChange(newPage + 1);
        }}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => {
          onPerPageChange(Number(e.target.value));
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: 'none',
          bgcolor: 'background.paper',
        }}
      />
    </>
  );
}
